import * as XLSX from "xlsx";
import * as pdfjs from "pdfjs-dist";
import * as mammoth from "mammoth";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

/**
 * Extracts text from various file types
 */
export const extractTextFromFile = async (file: File): Promise<string> => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  
  try {
    if (extension === 'pdf') {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      let fullText = "";
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(" ");
        fullText += pageText + "\n";
      }
      return fullText;
    } 
    
    if (['xlsx', 'xls', 'csv'].includes(extension || '')) {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      let fullContent = "";
      
      workbook.SheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName];
        // Use JSON for more structured data if it's an excel file, otherwise CSV
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        fullContent += `[Sheet: ${sheetName}]\n${JSON.stringify(data)}\n\n`;
      });
      return fullContent;
    }

    if (extension === 'docx') {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        return result.value;
    }

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    if (imageExtensions.includes(extension || '')) {
        return `[IMAGE ATTACHED: ${file.name} (${(file.size / 1024).toFixed(1)} KB)]\nNote: Visual content cannot be read directly as text. Please refer to the filename and any user description.`;
    }

    // Default to plain text
    return await file.text();
  } catch (error) {
    console.error(`Error extracting text from ${file.name}:`, error);
    return `[Error extracting text from ${file.name}]`;
  }
};

/**
 * History processing helper to minimize token usage and prevent context leakage
 * Strategy:
 * 1. Keep a sliding window of messages.
 * 2. Use a total character budget (e.g., 15,000 chars) for the entire history.
 * 3. Most recent messages get higher priority/limits.
 * 4. Mask large contents (files, tool results) in older turns earlier.
 */
export const formatMessageHistory = (dbMessages: any[]) => {
    // Keep a maximum window of 8 messages to limit token growth
    const MAX_MESSAGES = 8;
    // 60K chars ≈ 20K tokens — enough context while keeping LLM fast
    const TOTAL_BUDGET = 60000; 
    
    const messagesToProcess = dbMessages.slice(-MAX_MESSAGES);
    let remainingBudget = TOTAL_BUDGET;

    // Process messages from newest to oldest to prioritize recent context
    const processedMessages = [];
    
    for (let i = messagesToProcess.length - 1; i >= 0; i--) {
        const m = messagesToProcess[i];
        const age = (messagesToProcess.length - 1) - i;
        
        let content = m.content;
        
        // --- CONTENT OPTIMIZATION ---

        // 1. Handle File Attachments
        if (m.type === 'user' && content.includes('[ATTACHED FILE:')) {
            // Only keep file content for the most recent message (age 0)
            if (age > 0) {
                const parts = content.split('[ATTACHED FILE:');
                const fileName = (parts[1]?.split(']')[0] || 'Unknown File').trim();
                content = `${parts[0].trim()}\n[ATTACHED FILE: ${fileName}] (Content masked to save memory)`;
            }
        }

        // 2. Handle Tool Calls
        if (m.type === 'ai') {
            let aiParts = [m.content];
            if (m.tool_approval_data?.toolName) {
                const tName = m.tool_approval_data.toolName;
                const args = JSON.stringify(m.tool_approval_data.args);
                aiParts.push(`\n[TOOL: ${tName}(${args})]`);
            }
            
            // We NO LONGER include the result inside the AI message here, 
            // because it is already present as a separate 'system' turn (mapped to 'user' role)
            // in the history list. Including it twice confuses the model and wastes tokens.
            
            content = aiParts.join("");
        }
        
        if (m.type === 'system') {
            let resultText = content.replace('Execution Result: ', '');
            // System messages are usually tool results. Increase limit for recent results to handle bulk successes/data.
            // Age 0: 50,000, Age 1-3: 20,000, Older: 5,000 to prevent data loss in multi-step bulk operations.
            const sysLimit = age === 0 ? 50000 : (age <= 3 ? 20000 : 5000);
            if (resultText.length > sysLimit) {
                resultText = resultText.substring(0, sysLimit) + "... [Truncated]";
            }
            content = `[RESULT]: ${resultText}`;
        }

        // 3. Apply Cumulative Budget
        // Ensure each message doesn't take more than its fair share of what's left
        // (but older messages are processed later, so they naturally get less)
        // Age 0 (current) gets huge budget for files; Age 1-3 get decent space; older get minimal.
        const ageLimit = age === 0 ? 100000 : (age <= 3 ? 15000 : 2000);

        const finalLimit = Math.min(ageLimit, remainingBudget);
        
        if (content.length > finalLimit) {
            content = content.substring(0, finalLimit) + "...";
        }
        
        remainingBudget -= content.length;

        processedMessages.unshift({ 
            role: m.type === 'user' ? 'user' : (m.type === 'ai' ? 'assistant' : 'user'), 
            content: content.trim()
        });

        if (remainingBudget <= 0) break;
    }
    
    return processedMessages.filter(m => m.content.length > 0);
};

